import React, { useState } from 'react';

import { CustomOperatorValue } from '@aline/common';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import type { GridApiPremium, GridSortModel } from '@mui/x-data-grid-premium';
import { GridLogicOperator } from '@mui/x-data-grid-premium';
import { Clock, File, Signature, Star, Trash2, UserRound } from 'lucide-react';
import { DropdownSelectButton } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { useSelectCurrentUser } from 'src/gql/hooks/user';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

/** A view that filters or sorts the documents grid. */
export type RepositoryDocumentsView =
  | 'recently-created'
  | 'recently-accessed'
  | 'favorites'
  | 'myDocuments'
  | 'signedContracts';

/** Trash is not a filter: it swaps the documents grid for the trashed-documents grid. */
export type RepositoryView = RepositoryDocumentsView | 'trash';

interface ViewOption {
  icon: JSX.Element;
  label: string;
  value: RepositoryView;
}

const DOCUMENTS_VIEW_OPTIONS: ViewOption[] = [
  { icon: <Star size={16} />, label: 'Favorites', value: 'favorites' },
  { icon: <UserRound size={16} />, label: 'My documents', value: 'myDocuments' },
  { icon: <Signature size={16} />, label: 'Signed contracts', value: 'signedContracts' },
  { icon: <File size={16} />, label: 'Recently created', value: 'recently-created' },
  { icon: <Clock size={16} />, label: 'Recently accessed', value: 'recently-accessed' },
];

const TRASH_VIEW_OPTION: ViewOption = {
  icon: <Trash2 size={16} />,
  label: 'Trash',
  value: 'trash',
};

const VIEW_OPTIONS: ViewOption[] = [...DOCUMENTS_VIEW_OPTIONS, TRASH_VIEW_OPTION];

const RECENTLY_CREATED_SORT_MODEL: GridSortModel = [{ field: 'createdAt', sort: 'desc' }];

const RECENTLY_ACCESSED_SORT_MODEL: GridSortModel = [
  { field: 'lastAccessedByRequesterAt', sort: 'desc' },
];

const useStyles = makeStyles()(theme => ({
  menuIcon: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    minWidth: '0 !important',
    paddingRight: theme.spacing(1),
  },
}));

interface RepositoryViewsDropdownProps {
  activeView: RepositoryView;
  onViewChange: (view: RepositoryView) => void;
}

export function RepositoryViewsDropdown({
  activeView,
  onViewChange,
}: RepositoryViewsDropdownProps): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const activeOption = VIEW_OPTIONS.find(opt => opt.value === activeView) ?? VIEW_OPTIONS[0];
  const handleSelect = (view: RepositoryView) => {
    setMenuOpen(false);
    onViewChange(view);
  };

  return (
    <>
      <DropdownSelectButton
        ref={setAnchorEl}
        icon={activeOption.icon}
        label={activeOption.label}
        selected={menuOpen}
        variant="outlined"
        onClick={() => setMenuOpen(!menuOpen)}
      />
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={menuOpen}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={() => setMenuOpen(false)}
      >
        {DOCUMENTS_VIEW_OPTIONS.map(opt => (
          <ViewMenuItem
            key={opt.value}
            activeView={activeView}
            option={opt}
            onSelect={handleSelect}
          />
        ))}
        <Divider />
        <ViewMenuItem activeView={activeView} option={TRASH_VIEW_OPTION} onSelect={handleSelect} />
      </Menu>
    </>
  );
}

/**
 * Apply a documents view's filter and sort models to the documents grid.
 * Trash is handled by the parent, which swaps in the trashed-documents grid.
 */
export function useApplyRepositoryView(apiRef: React.RefObject<GridApiPremium | null>) {
  const currentUser = useSelectCurrentUser();

  return React.useCallback(
    (view: RepositoryDocumentsView) => {
      const api = apiRef.current;
      if (!api) return;

      switch (view) {
        case 'recently-created':
          api.setFilterModel({ items: [] });
          api.setSortModel(RECENTLY_CREATED_SORT_MODEL);
          return;
        case 'recently-accessed':
          api.setFilterModel({ items: [] });
          api.setSortModel(RECENTLY_ACCESSED_SORT_MODEL);
          return;
        case 'favorites':
          api.setFilterModel({
            items: [
              {
                field: Apollo.ReportColumnFileField.RequesterFavorited,
                id: uuid4(),
                operator: 'is',
                value: 'true',
              },
            ],
            logicOperator: GridLogicOperator.And,
          });
          return;
        case 'myDocuments':
          if (!currentUser) return;
          api.setFilterModel({
            items: [
              {
                field: Apollo.ReportColumnFileField.Owner,
                id: uuid4(),
                operator: CustomOperatorValue.Owner,
                value: [
                  {
                    __typename: 'User',
                    avatarColor: currentUser.avatarColor,
                    displayName: currentUser.displayName,
                    id: currentUser.id,
                  },
                ],
              },
            ],
            logicOperator: GridLogicOperator.And,
          });
          return;
        case 'signedContracts':
          api.setFilterModel({
            items: [
              {
                field: 'status',
                id: uuid4(),
                operator: CustomOperatorValue.DocumentStatus,
                value: [Apollo.DocumentStatus.Signed],
              },
            ],
            logicOperator: GridLogicOperator.And,
          });
          return;
      }
    },
    [apiRef, currentUser]
  );
}

interface ViewMenuItemProps {
  activeView: RepositoryView;
  onSelect: (view: RepositoryView) => void;
  option: ViewOption;
}

function ViewMenuItem({ activeView, onSelect, option }: ViewMenuItemProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <MenuItem selected={option.value === activeView} onClick={() => onSelect(option.value)}>
      <ListItemIcon className={classes.menuIcon}>{option.icon}</ListItemIcon>
      <ListItemText primary={option.label} />
    </MenuItem>
  );
}
